<template>
	<svg
		class="decorative-text-divider"
		:viewBox="`0 0 ${width} ${height}`"
		:width="width"
		:height="height"
		preserveAspectRatio="xMidYMid meet"
	>
		<defs>
			<linearGradient
				id="fade-left"
				gradientUnits="userSpaceOnUse"
				:x1="leftLineEnd"
				y1="0"
				:x2="leftLineStart"
				y2="0"
			>
				<stop offset="0%" :stop-color="color" stop-opacity="0" />
				<stop offset="100%" :stop-color="color" stop-opacity="1" />
			</linearGradient>

			<linearGradient
				id="fade-right"
				gradientUnits="userSpaceOnUse"
				:x1="rightLineEnd"
				y1="0"
				:x2="rightLineStart"
				y2="0"
			>
				<stop offset="0%" :stop-color="color" stop-opacity="1" />
				<stop offset="100%" :stop-color="color" stop-opacity="0" />
			</linearGradient>
		</defs>

		<!-- left line -->
		<line
			:x1="leftLineStart"
			:y1="centerY"
			:x2="leftLineEnd"
			:y2="centerY"
			stroke="url(#fade-left)"
			:stroke-width="strokeWidth"
			stroke-linecap="square"
		/>

		<!-- right line -->
		<line
			:x1="rightLineStart"
			:y1="centerY"
			:x2="rightLineEnd"
			:y2="centerY"
			stroke="url(#fade-right)"
			:stroke-width="strokeWidth"
			stroke-linecap="square"
		/>

		<!-- left diamond -->
		<polygon
			:points="diamondPoints(leftDiamondX)"
			fill="none"
			:stroke="color"
			:stroke-width="strokeWidth"
			stroke-linejoin="miter"
		/>

		<!-- right diamond -->
		<polygon
			:points="diamondPoints(rightDiamondX)"
			fill="none"
			:stroke="color"
			:stroke-width="strokeWidth"
			stroke-linejoin="miter"
		/>

		<!-- center text -->
		<!-- 
			NOTE: Optical Y-offset (+2) to compensate for non-standard font ascent.
			Without this, the glyphs appear visually too high and look clipped at the top
			when using dominant-baseline="middle".
		-->
		<text
			ref="textEl"
			:x="centerX"
			:y="centerY + 2"
			text-anchor="middle"
			dominant-baseline="middle"
			:fill="color"
			:font-size="fontSize"
			:style="{
				fontWeight: fontWeight,
				textShadow: '0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6)',
			}"
		>
			{{ label }}
		</text>
	</svg>
</template>

<script setup lang="ts">
import { FontWeights } from "@/types/ui";
import { computed, nextTick, onMounted, ref, watch } from "vue";

const props = defineProps({
	label: { type: String, default: "OR" },
	color: { type: String, default: "#372615" },
	width: { type: Number, default: 800 },
	height: { type: Number, default: 40 },
	strokeWidth: { type: Number, default: 3 },
	fontSize: { type: Number, default: 40 },
	fontWeight: { type: Number, default: FontWeights.BOLD },
	padding: { type: Number, default: 24 }, // offset diamonds from the edge
	diamondSize: { type: Number, default: 8 }, // FIXED diamond size
	textGap: { type: Number, default: 30 }, // minimum space around text
});

const textEl = ref<SVGTextElement | null>(null);
const textWidth = ref(0);

const centerX = computed((): number => props.width / 2);
const centerY = computed((): number => props.height / 2);

const leftDiamondX = computed((): number => props.padding);
const rightDiamondX = computed((): number => props.width - props.padding);

// real text width (SVG getBBox)
const updateTextWidth = (): void => {
	if (!textEl.value) return;
	textWidth.value = textEl.value.getBBox().width;
};

onMounted(async (): Promise<void> => {
	await nextTick();
	updateTextWidth();
});

watch(
	(): string => props.label,
	async (): Promise<void> => {
		await nextTick();
		updateTextWidth();
	},
);

// line ends – NEVER touch the diamond
const halfTextBlock = computed(
	(): number => textWidth.value / 2 + props.textGap,
);

const leftLineStart = computed(
	(): number => leftDiamondX.value + props.diamondSize,
);
const leftLineEnd = computed((): number => centerX.value - halfTextBlock.value);

const rightLineStart = computed(
	(): number => centerX.value + halfTextBlock.value,
);
const rightLineEnd = computed(
	(): number => rightDiamondX.value - props.diamondSize,
);

const diamondPoints = (x: number): string => {
	const s = props.diamondSize;
	const y = centerY.value;
	return `
		${x},${y - s}
		${x + s},${y}
		${x},${y + s}
		${x - s},${y}
	`;
};
</script>

<style lang="scss" scoped>
.decorative-text-divider {
	display: block;
	width: 100%;
	height: auto;
}
</style>
